import { LottieRefCurrentProps } from 'lottie-react'
import React, { MutableRefObject, useEffect, useRef, useState } from 'react'

import { setIntersectionObserver } from '../../utils/setIntersectionObserver'
import { ExplainerStyles } from './ExplainerStyles'
import { MoneyBackExplainer } from './MoneyBackExplainer'
import { MonthlyExplainer } from './MonthlyExplainer'
import { AnimationSteps, TimeoutId } from './types'

interface LottieAnimationExplainerProps {
  isRefundAtRenewal: boolean
}

export const LottieAnimationExplainer = ({ isRefundAtRenewal }: LottieAnimationExplainerProps) => {
  const lottieRef = useRef<LottieRefCurrentProps & { currentTime: number }>(null)
  const annimationTriggerRef = useRef<HTMLDivElement>(null)
  const intervalRef: MutableRefObject<TimeoutId> = useRef(null)
  const timeoutRef = useRef<TimeoutId>(null)

  const [activeStep, setActiveStep] = useState<AnimationSteps>(AnimationSteps.One)
  const [animationProgress, setAnimationProgress] = useState(0)

  const animationBreakpoints = isRefundAtRenewal ? [7.4, 22, 37.5] : [3.9, 7, 20.4]

  const startRange = {
    1: 0,
    2: animationBreakpoints[0],
    3: animationBreakpoints[1],
  }

  const breakpointRange = {
    1: animationBreakpoints[0],
    2: animationBreakpoints[1],
    3: animationBreakpoints[2],
  }

  const handlePlayStep = (step: AnimationSteps) => {
    if (lottieRef.current && lottieRef.current.animationItem) {
      lottieRef.current.animationItem.goToAndPlay(startRange[step] * 1000, false)
      setAnimationProgress(0)
      setActiveStep(step)
    }
  }

  const handleLottieUpdates = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    if (lottieRef.current && lottieRef.current.animationItem) {
      const breakpoint = breakpointRange[activeStep]
      const animation = lottieRef.current.animationItem
      const startTime = activeStep === AnimationSteps.One ? 0 : animation.currentFrame / animation.frameRate

      intervalRef.current = setInterval(function () {
        const currentTime = animation.currentFrame / animation.frameRate

        const range = breakpoint - startTime
        const progress = currentTime - startTime

        const stepComplete = currentTime > breakpoint
        const percentage = stepComplete ? 100 : (progress / range) * 100

        setAnimationProgress(percentage)
        if (currentTime >= breakpoint && intervalRef.current) {
          lottieRef.current && lottieRef.current.pause()
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      }, 500)
    }
  }

  useEffect(() => {
    // Timeout is required here to match with the one in the monthly explainer fixing dynamic value formating
    // Otherwise the target element does not yet exist when this runs
    timeoutRef.current = setTimeout(() => {
      const targetElement = annimationTriggerRef.current
      if (targetElement) {
        setIntersectionObserver({
          targetElement,
          onIntersecting: () => {
            handleLottieUpdates()
            handlePlayStep(AnimationSteps.One)
          },
        })
      }
    }, 50)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    handleLottieUpdates()
  }, [activeStep])

  const props = {
    lottieRef,
    activeStep,
    handlePlayStep,
    animationProgress,
    annimationTriggerRef,
  }

  return (
    <ExplainerStyles>
      {isRefundAtRenewal ? <MoneyBackExplainer {...props} /> : <MonthlyExplainer {...props} />}
    </ExplainerStyles>
  )
}
